import { DocumentSnapshot, DocumentData } from '@angular/fire/firestore';

/** Onboarding state for model users */
export interface ModelOnboardingState {
  completed: boolean;
  acknowledged: boolean;
  completedAt?: any; // Firestore Timestamp
}

/** Onboarding state for client users */
export interface ClientOnboardingState {
  completed: boolean;
  acknowledged: boolean;
}

/** Structured onboarding field */
export interface UserOnboarding {
  model?: ModelOnboardingState;
  client?: ClientOnboardingState;
}

export interface UserFirestoreData {
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_verified: boolean;
  has_profile: boolean;
  is_model: boolean;
  is_admin: boolean;
  referral_code?: string | null;
  referredBy?: string | null;
  onboarding?: UserOnboarding;
}

export class User {
  user_id = '';
  email: string | null = null;
  display_name: string | null = null;
  is_verified = false;
  has_profile = false;
  is_model = false;
  is_admin = false;
  referral_code: string | null = null;
  referredBy: string | null = null;
  onboarding?: UserOnboarding;

  constructor(init?: Partial<User>) {
    Object.assign(this, init);
  }

  /**
   * Convert Firestore document → User model
   * Supports both DocumentSnapshot and plain objects
   */
  static fromFirestore(
    input: DocumentSnapshot<DocumentData> | (DocumentData & { id?: string })
  ): User {
    const data = 'data' in input ? input.data() || {} : input;
    const id = 'id' in input ? input.id : input['id'];

    // Parse onboarding field if present
    let onboarding: UserOnboarding | undefined;
    if (data['onboarding']) {
      onboarding = {
        model: data['onboarding']['model'] ? {
          completed: data['onboarding']['model']['completed'] ?? false,
          acknowledged: data['onboarding']['model']['acknowledged'] ?? false,
          completedAt: data['onboarding']['model']['completedAt'] ?? null
        } : undefined,
        client: data['onboarding']['client'] ? {
          completed: data['onboarding']['client']['completed'] ?? false,
          acknowledged: data['onboarding']['client']['acknowledged'] ?? false
        } : undefined
      };
    }

    return new User({
      user_id: (data['user_id'] as string) ?? id ?? '',
      email: (data['email'] as string) ?? null,
      display_name: (data['display_name'] as string) ?? null,
      is_verified: (data['is_verified'] as boolean) ?? false,
      has_profile: (data['has_profile'] as boolean) ?? false,
      is_model: (data['is_model'] as boolean) ?? false,
      is_admin: (data['is_admin'] as boolean) ?? false,
      referral_code: (data['referral_code'] as string) ?? null,
      referredBy: (data['referredBy'] as string) ?? null,
      onboarding
    });
  }

  /**
   * Convert model → JSON object to write to Firestore
   */
  toFirestore(): UserFirestoreData {
    const data: UserFirestoreData = {
      user_id: this.user_id,
      email: this.email,
      display_name: this.display_name,
      is_verified: this.is_verified,
      has_profile: this.has_profile,
      is_model: this.is_model,
      is_admin: this.is_admin,
      referral_code: this.referral_code ?? null,
      referredBy: this.referredBy ?? null,
    };

    // Only include onboarding if defined
    if (this.onboarding) {
      data.onboarding = this.onboarding;
    }

    return data;
  }

  /**
   * Check if model onboarding is complete (with backward compatibility)
   * Falls back to has_profile for users without onboarding field
   */
  isModelOnboardingComplete(): boolean {
    if (this.onboarding?.model?.completed !== undefined) {
      return this.onboarding.model.completed;
    }
    // Backward compatibility: if has_profile is true, treat as completed
    return this.has_profile && this.is_model;
  }

  /**
   * Check if model onboarding banner should be shown
   */
  shouldShowModelOnboardingBanner(): boolean {
    // Show banner only if explicitly completed but not yet acknowledged
    if (this.onboarding?.model) {
      return this.onboarding.model.completed && !this.onboarding.model.acknowledged;
    }
    // No onboarding field → user predates the onboarding system; don't show banner
    return false;
  }
}
